"use server";

import { z } from "zod";
import { revalidatePath } from "next/cache";
import { and, eq, sql } from "drizzle-orm";
import { db, schema } from "@/db";
import { env } from "@/lib/env";
import { NOTE_COLOR_KEYS } from "@/lib/colors";
import { ensureDevUser } from "@/lib/queries";

// --- helpers ------------------------------------------------------------
const USER = env.DEV_USER_ID;

type Result<T = undefined> =
  | (T extends undefined ? { ok: true } : { ok: true; data: T })
  | { ok: false; error: string };

function fail(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

/** Confirma que la nota pertenece al usuario de desarrollo. */
async function ownsNote(id: string): Promise<boolean> {
  const row = await db
    .select({ id: schema.notes.id })
    .from(schema.notes)
    .where(and(eq(schema.notes.id, id), eq(schema.notes.userId, USER)))
    .limit(1);
  return row.length > 0;
}

// --- validación ---------------------------------------------------------
const ColorSchema = z.enum(NOTE_COLOR_KEYS as [string, ...string[]]);

const CreateNote = z.object({
  title: z.string().trim().max(255).optional().default(""),
  content: z.string().max(20000).optional().default(""),
  color: ColorSchema.optional().default("default"),
});

const UpdateNote = z.object({
  id: z.string().uuid(),
  title: z.string().trim().max(255).optional(),
  content: z.string().max(20000).optional(),
  color: ColorSchema.optional(),
  pinned: z.boolean().optional(),
});

const Id = z.string().uuid();
const TagName = z.string().trim().min(1, "La etiqueta no puede estar vacía").max(50);

// --- notas: CRUD --------------------------------------------------------
export async function createNote(input: z.input<typeof CreateNote>): Promise<Result<{ id: string }>> {
  const parsed = CreateNote.safeParse(input);
  if (!parsed.success) return fail(parsed.error.issues[0]?.message ?? "Datos inválidos");
  const { title, content, color } = parsed.data;
  if (!title.trim() && !content.trim()) return fail("La nota está vacía");

  await ensureDevUser();
  const [row] = await db
    .insert(schema.notes)
    .values({ userId: USER, title: title || null, content, color: color === "default" ? null : color })
    .returning({ id: schema.notes.id });
  revalidatePath("/");
  return { ok: true, data: { id: row.id } };
}

export async function updateNote(input: z.input<typeof UpdateNote>): Promise<Result> {
  const parsed = UpdateNote.safeParse(input);
  if (!parsed.success) return fail(parsed.error.issues[0]?.message ?? "Datos inválidos");
  const { id, title, content, color, pinned } = parsed.data;
  if (!(await ownsNote(id))) return fail("Nota no encontrada");

  await db
    .update(schema.notes)
    .set({
      ...(title !== undefined ? { title: title || null } : {}),
      ...(content !== undefined ? { content } : {}),
      ...(color !== undefined ? { color: color === "default" ? null : color } : {}),
      ...(pinned !== undefined ? { pinned } : {}),
      updatedAt: new Date(),
    })
    .where(eq(schema.notes.id, id));
  revalidatePath("/");
  return { ok: true };
}

export async function softDeleteNote(id: string): Promise<Result> {
  if (!Id.safeParse(id).success || !(await ownsNote(id))) return fail("Nota no encontrada");
  await db.update(schema.notes).set({ deletedAt: new Date() }).where(eq(schema.notes.id, id));
  revalidatePath("/");
  return { ok: true };
}

export async function restoreNote(id: string): Promise<Result> {
  if (!Id.safeParse(id).success || !(await ownsNote(id))) return fail("Nota no encontrada");
  await db.update(schema.notes).set({ deletedAt: null }).where(eq(schema.notes.id, id));
  revalidatePath("/");
  return { ok: true };
}

export async function deleteNoteForever(id: string): Promise<Result> {
  if (!Id.safeParse(id).success || !(await ownsNote(id))) return fail("Nota no encontrada");
  await db.delete(schema.notes).where(eq(schema.notes.id, id));
  revalidatePath("/");
  return { ok: true };
}

export async function emptyTrash(): Promise<Result> {
  await db
    .delete(schema.notes)
    .where(and(eq(schema.notes.userId, USER), sql`${schema.notes.deletedAt} is not null`));
  revalidatePath("/");
  return { ok: true };
}

// --- etiquetas ----------------------------------------------------------
export async function addTagToNote(noteId: string, name: string): Promise<Result> {
  if (!Id.safeParse(noteId).success || !(await ownsNote(noteId))) return fail("Nota no encontrada");
  const parsedName = TagName.safeParse(name);
  if (!parsedName.success) return fail(parsedName.error.issues[0]?.message ?? "Etiqueta inválida");
  const tagName = parsedName.data;

  await ensureDevUser();
  // upsert de la etiqueta (única por usuario) y recuperación de su id
  const [tag] = await db
    .insert(schema.tags)
    .values({ userId: USER, name: tagName })
    .onConflictDoUpdate({ target: [schema.tags.userId, schema.tags.name], set: { name: tagName } })
    .returning({ id: schema.tags.id });

  await db.insert(schema.noteTags).values({ noteId, tagId: tag.id }).onConflictDoNothing();
  await db.update(schema.notes).set({ updatedAt: new Date() }).where(eq(schema.notes.id, noteId));
  revalidatePath("/");
  return { ok: true };
}

export async function removeTagFromNote(noteId: string, tagId: string): Promise<Result> {
  if (!Id.safeParse(noteId).success || !Id.safeParse(tagId).success) return fail("Datos inválidos");
  if (!(await ownsNote(noteId))) return fail("Nota no encontrada");
  await db
    .delete(schema.noteTags)
    .where(and(eq(schema.noteTags.noteId, noteId), eq(schema.noteTags.tagId, tagId)));

  // limpia etiquetas huérfanas (sin notas asociadas) para no acumular basura
  await db.execute(sql`
    delete from ${schema.tags} t
    where t.id = ${tagId}
      and t.user_id = ${USER}
      and not exists (select 1 from ${schema.noteTags} nt where nt.tag_id = t.id)
  `);
  revalidatePath("/");
  return { ok: true };
}
